import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {DesignRef} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {VisualArtifactRef} from '@mintdata-wsp/common/state/ui/visualDesigner/artifacts/VisualArtifactRef';
import {DesignState, DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {Overlay} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Overlay';
import {Page, PageId, PageName} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/Page';
import {EventProp} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/EventProp';
import {Prop} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {Repeater} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/repeater/Repeater';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {OpenDesignState, SampleDesignState} from '@mintdata-wsp/common/state/ui/visualDesigner/VisualDesignerState';
import {
    ALoadedEvent,
    APageAppearingEvent,
    APostMessageEvent,
    AResizeEvent
} from '@mintdata/mint-kit/api/events/EventType';
import {BoxId} from '@mintdata/mint-kit/api/models/Identifiers';
import {PropPath} from '@mintdata/mint-kit/api/properties/PropPath';
import bind from 'bind-decorator';
import {action} from 'mobx';
import {HasEvents, EventBus} from '@mintdata-wsp/common/bus/EventBus';

type DesignStateListener = {
    onActivePageChanged():void;
}

export class DesignStateController implements HasEvents<DesignStateListener>
{
    @bind
    public getDesignState():DesignState | null
    {
        const openDesign = this.getOpenDesign();
        return openDesign ? openDesign.designState : null;
    }

    @action.bound
    public setDesignState(newState:DesignState)
    {
        const openDesign = this.getOpenDesign();
        if( openDesign && openDesign.initialized )
            openDesign.designState = newState;
    }

    @bind
    public getActivePage():Page | null
    {
        const designState:DesignState = this.getDesignState();

        if( !designState || !designState.surface )
            return null;

        return designState.surface.activePage;
    }

    @action
    public setOpenDesign(openDesign:OpenDesignState):void
    {
        rootState.ui.visualDesigner.openDesign = openDesign;
    }

    @action
    public setSampleDesignMode(setSampleDesignMode:boolean | null = true):void
    {
        rootState.ui.visualDesigner.sampleDesign = setSampleDesignMode ? new SampleDesignState() : null;
    }

    public isInSampleDesignMode():boolean
    {
        return !!rootState.ui.visualDesigner.sampleDesign;
    }

    public isInSampleDesignEmbeddedMode():boolean
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        return sampleDesign && sampleDesign.embedded;
    }

    @action
    public setSampleDesignParam(sampleDesignParam:string, value:any):void
    {
        rootState.ui.visualDesigner.sampleDesign[sampleDesignParam] = value;
    }

    @bind
    public getOpenDesign():OpenDesignState | null
    {
        const openDesign = rootState.ui.visualDesigner.openDesign;
        return openDesign ? openDesign : null;
    }

    @action.bound
    public addVisualArtifact(vaRef:VisualArtifactRef):void
    {
        const {designState} = this.getOpenDesign();

        if(
            !designState.visualArtifactsRefs.find((va:VisualArtifactRef) =>
            {
                return VisualArtifactRef.makeId(va.organization, va.name) === vaRef.id;
            })
        )
            designState.visualArtifactsRefs.push(vaRef);
    }

    @bind
    public findBoxFromId(boxId:BoxId):Box | null
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return null;

        const pagesArray:Page[] = Array.from(designState.surface.pages.values());

        for( let i = 0; i < pagesArray.length; i++ )
        {
            if( pagesArray[i].artboard.allBoxes.has(boxId) )
                return pagesArray[i].artboard.allBoxes.get(boxId);
        }

        return null;
    }

    @action.bound
    public changeBoxesHostIdOnPropChange(prop:Prop<any>, propPath:PropPath, newValue:any, oldValue:any):void
    {
        const box = prop.box;

        if( !box ) return;

        const componentDesc = prop.box.componentDesc;
        const {componentConfig, containerPropertyConfig} = componentDesc;

        if( !containerPropertyConfig || !componentConfig.supportsChildren || containerPropertyConfig.propertyName !== prop.name )
            return;

        const itemsPath = PropPath.fromFieldPath(containerPropertyConfig.itemsPath);
        const idPath = PropPath.fromFieldPath(containerPropertyConfig.idPath);

        // if propPath is like this: `itemsPath.<index>.idPath`
        if( itemsPath.isPrefixOf(propPath)
            && idPath.isSuffixOf(propPath)
            && propPath.length() == itemsPath.length() + 1 + idPath.length()
        )
        {
            const oldHostId = oldValue.toString();
            const newHostId = newValue.toString();
            box.children
                .filter(child => child.hostId === oldHostId)
                .forEach(child => child.setHostId(newHostId));
        }
    }

    @action.bound
    public removeHostOnPropArrayItemRemoved(prop:Prop<any>, propPath:PropPath, index:number):void
    {
        const box = prop.box;

        const componentDesc = prop.box.componentDesc;
        const {componentConfig, containerPropertyConfig} = componentDesc;

        if( !componentConfig.supportsChildren || !containerPropertyConfig || containerPropertyConfig.propertyName !== prop.name )
            return;

        if( propPath.isEqualToFP(containerPropertyConfig.itemsPath) )
        {
            const removedHostId = componentDesc.containerHostIds[index];

            box.children
                .filter(child => child.hostId === removedHostId)
                .forEach(box.removeChild);
        }
    }

    @action.bound
    public setActivePage(page:Page, isFromSheetFunction:boolean = true, isAnimated:boolean = true):boolean
    {
        if( !page || !page.artboard )
            return false;

        const state:DesignState = this.getDesignState();
        const currentActivePage:Page = this.getActivePage();

        if( isFromSheetFunction && currentActivePage && currentActivePage.artboard && currentActivePage.artboard.viewport )
            currentActivePage.artboard.viewport.componentDesc.propsValues.onPageHidden(
                new APageAppearingEvent(currentActivePage.name, false)
            );

        state.surface.setAllowPagesAnimation(isFromSheetFunction && isAnimated);
        state.surface.setActivePage(page);

        if( isFromSheetFunction && page.artboard.viewport )
            page.artboard.viewport.componentDesc.propsValues.onPageShown(new APageAppearingEvent(page.name, false));

        return true;
    }

    @bind
    public onActivePageChanged()
    {
        EventBus.fire(this, 'activePageChanged');
    }

    @action.bound
    public showOverlay(pageToBeAnOverlay:Page, isFromSheetFunction:boolean = true, isAnimated:boolean = true):boolean
    {
        const state:DesignState = this.getDesignState();
        const {activePage} = state.surface;
        state.surface.setAllowPagesAnimation(isFromSheetFunction && isAnimated);

        if( !pageToBeAnOverlay || activePage.id === pageToBeAnOverlay.id )
            return false;

        const index:number = this.getIndexInOverlaysByPage(pageToBeAnOverlay);
        const overlay:Overlay = Overlay.create(pageToBeAnOverlay);

        activePage.addOverlay(overlay, index);

        if( isFromSheetFunction && pageToBeAnOverlay.artboard && pageToBeAnOverlay.artboard.viewport )
            pageToBeAnOverlay.artboard.viewport.componentDesc.propsValues.onPageShown(
                new APageAppearingEvent(pageToBeAnOverlay.name, true)
            );

        return true;
    }

    @action.bound
    public hideOverlay(overlayIndex:number, hideAllAbove:boolean, isFromSheetFunction:boolean = true, isAnimated:boolean = true, page?:Page):boolean
    {
        const state:DesignState = this.getDesignState();
        const pageOverlayHolder:Page = page ? page : state.surface.activePage;

        if( overlayIndex === -1 || pageOverlayHolder.overlays[overlayIndex].isPinned )
            return false;

        state.surface.setAllowPagesAnimation(isFromSheetFunction && isAnimated);

        if( hideAllAbove )
            pageOverlayHolder.removeOverlaysByPredicate((overlay:Overlay, index:number) =>
                {
                    if( index >= overlayIndex && !overlay.isPinned )
                    {
                        if( isFromSheetFunction && overlay.page && overlay.page.artboard && overlay.page.artboard.viewport )
                            overlay.page.artboard.viewport.componentDesc.propsValues.onPageHidden(
                                new APageAppearingEvent(overlay.page.name, true)
                            );

                        return true;
                    }

                    return false;
                }
            );
        else
        {
            const pageFromRemovedOv:Page = pageOverlayHolder.removeOverlay(overlayIndex);

            if( isFromSheetFunction && pageFromRemovedOv && pageFromRemovedOv.artboard && pageFromRemovedOv.artboard.viewport )
                pageFromRemovedOv.artboard.viewport.componentDesc.propsValues.onPageHidden(
                    new APageAppearingEvent(pageFromRemovedOv.name, true)
                );
        }

        return true;
    }

    @bind
    public getActivePageName():string
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return null;

        return designState.surface.activePage.name;
    }

    @bind
    public setActivePageByName(name:PageName, isAnimated:boolean = true):boolean
    {
        const designState = this.getDesignState();
        if( !designState || !designState.surface )
            return false;

        return this.setActivePage(commonControllers.surfaceArea.findPageByName(name), true, isAnimated);
    }

    @bind
    public setActivePageById(id:PageId, isAnimated:boolean = true):boolean
    {
        const designState = this.getDesignState();
        if( !designState || !designState.surface )
            return false;

        return this.setActivePage(commonControllers.surfaceArea.findPageById(id), true, isAnimated);
    }

    @bind
    public showOverlayByName(name:PageName, isAnimated:boolean):boolean
    {
        const designState = this.getDesignState();
        if( !designState || !designState.surface )
            return false;

        const page:Page = commonControllers.surfaceArea.findPageByName(name);

        return this.showOverlay(page, true, isAnimated);
    }

    @bind
    public hideOverlayByName(name:PageName, hideAllAbove:boolean, isAnimated:boolean = true):boolean
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return false;

        const page:Page = commonControllers.surfaceArea.findPageByName(name);
        const index:number = this.getIndexInOverlaysByPage(page);

        return this.hideOverlay(index, hideAllAbove, true, isAnimated);
    }

    @action.bound
    public toggleOverlay(name:PageName, hideAllAbove:boolean, isAnimated:boolean):boolean
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return false;

        const activePage:Page = designState.surface.activePage;
        const page:Page = commonControllers.surfaceArea.findPageByName(name);

        if( !page || activePage.id === page.id )
            return false;

        const indexOfToggled:number = this.getIndexInOverlaysByPage(page);

        if( indexOfToggled === -1 )
            this.showOverlay(page, true, isAnimated);
        else
            this.hideOverlay(indexOfToggled, hideAllAbove, true, isAnimated);

        return true;
    }

    @bind
    public hideTopmostOverlay(isAnimated:boolean):boolean
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return false;

        const activePage:Page = designState.surface.activePage;
        const {length} = activePage.overlays;

        if( length === 0 || activePage.overlays[length - 1].isPinned )
            return false;

        return this.hideOverlay(length - 1, true, true, isAnimated);
    }

    @action.bound
    public hideAllOverlays(isFromSheetFunction:boolean = true, isAnimated:boolean):boolean
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return false;

        const activePage:Page = designState.surface.activePage;
        const {length} = activePage.overlays;

        if( length === 0 )
            return false;

        designState.surface.setAllowPagesAnimation(isFromSheetFunction && isAnimated);

        let counterOfHidden:number = 0;
        activePage.removeOverlaysByPredicate(({isPinned, page}:Overlay) =>
            {
                if( !isPinned )
                {
                    counterOfHidden++;

                    if( page && page.artboard && page.artboard.viewport )
                        page.artboard.viewport.componentDesc.propsValues.onPageHidden(
                            new APageAppearingEvent(page.name, true)
                        );

                    return true;
                }

                return false;
            }
        );

        return counterOfHidden > 0;
    }

    @bind
    public getIndexInOverlaysByPage(page:Page):number
    {
        if( this.getDesignState().surface && this.getDesignState().surface.activePage )
        {
            const {overlays} = this.getDesignState().surface.activePage;

            for( let i = 0; i < overlays.length; i++ )
            {
                if( page.id === overlays[i].page.id )
                    return i;
            }
        }

        return -1;
    }

    @bind
    public isTheTopmostOverlay(pageId:PageId):boolean
    {
        const designState = this.getDesignState();

        if( !designState || !designState.surface || !designState.surface.activePage )
            return null;

        const activePage:Page = designState.surface.activePage;
        const {length} = activePage.overlays;

        return length > 0 && activePage.overlays[length - 1].page.id === pageId;
    }

    @bind
    public onApplicationLoad():void
    {
        const {surfaceArea} = commonControllers;
        const designState = this.getDesignState();

        if( !designState || !designState.surface )
            return null;

        const designRef:DesignRef = this.getOpenDesign().designRef;

        const appLoadEventProp:EventProp = designState.surface.eventPropsByName.get(SystemPropsConfigs.APPLICATION_LOAD_EVENT_PROP_NAME);

        surfaceArea.onBaseEvent(
            appLoadEventProp,
            Repeater.initial(),
            new ALoadedEvent(designRef.designName, null),
            designRef.designId.toString()
        );
    }

    @bind
    public onViewportResize(width:number, height:number, type:string):void
    {
        const {surfaceArea} = commonControllers;
        const designState = this.getDesignState();

        if( !designState || !designState.surface )
            return null;

        const designRef:DesignRef = this.getOpenDesign().designRef;

        const viewportResizeEventProp:EventProp = designState.surface.eventPropsByName.get(SystemPropsConfigs.VIEWPORT_RESIZE_EVENT_PROP_NAME);

        surfaceArea.onBaseEvent(
            viewportResizeEventProp,
            Repeater.initial(),
            new AResizeEvent(width, height, type),
            designRef.designId.toString()
        );
    }

    @action.bound
    public onPostMessage(event:MessageEvent):void
    {
        if( event.source !== window && event.data )
        {
            const {surfaceArea} = commonControllers;
            const designState = this.getDesignState();

            if( !designState || !designState.surface )
                return null;

            const designRef:DesignRef = this.getOpenDesign().designRef;

            const postMessageEventProp:EventProp = designState.surface.eventPropsByName.get(SystemPropsConfigs.POST_MESSAGE_PROP_NAME);

            surfaceArea.onBaseEvent(
                postMessageEventProp,
                Repeater.initial(),
                new APostMessageEvent(event.data),
                designRef.designId.toString()
            );
        }
    }

    public addPostMessageListener():void
    {
        window.addEventListener('message', this.onPostMessage, false);
    }

    public removePostMessageListener():void
    {
        window.removeEventListener('message', this.onPostMessage, false);
    }

    @action.bound
    private _onHashChange(event:HashChangeEvent)
    {
        const openDesign = this.getOpenDesign();

        if( !openDesign )
            return;

        const result:RegExpExecArray = /^(?:.+#)(.*)/.exec(event.newURL);
        const hash:string = result && result[1] ? result[1] : '';

        if( openDesign.locationHash != hash )
            openDesign.locationHash = hash;
    }

    public addHashListener():void
    {
        window.addEventListener('hashchange', this._onHashChange, false);
    }

    public removeHashListener():void
    {
        window.removeEventListener('hashchange', this._onHashChange, false);
    }

    @action
    public processDesignStateFromJsonCtx(ctx:DesignStateFromJsonCtx):void
    {
        ctx.addedSheets.forEach((sheet:AnySheet) => commonControllers.spreadsheets.workbook.initializeSheet(sheet));
        ctx.removedSheets.forEach((sheet:AnySheet) => commonControllers.spreadsheets.workbook.terminateSheet(sheet));

        ctx.addedSlots.forEach((slot:Slot) =>
        {
            commonControllers.spreadsheets.sheet.initializeSlot(slot);
            setTimeout(() => commonControllers.spreadsheets.sheet.reEvaluateSlot(slot.sheet.id, slot.location));
        });

        ctx.removedSlots.forEach((slot:Slot) => commonControllers.spreadsheets.sheet.terminateSlot(slot));
    }
}
